const DEFAULT_LISTICLE_SELECTOR = '.rebellt-item';
const ARTICLE_NON_EDITORIAL_SELECTOR = [
  '.rm-embed-container', '.rm-shortcode', '.rm-embed-holder', '.rm-embed-spacer',
  'iframe', '.instagram-media', 'figure', 'figcaption', '.image-caption', '.image-credit',
  '.around-the-web', '.related-post', '.posts-custom', '.post-pager',
  '.ad-wrapper', '.content-ad', '.separator', '.stories-player-container',
  '.inline-newsletter-box', '.inline-newsletter-box-end', '.milla-newsletter-embed',
  '.milla-contest-embed', '.milla-insights-embed', '[hidden]', '[aria-hidden="true"]'
].join(', ');
const ARTICLE_ATOMIC_SECTION_SELECTOR = '.rebellt-item, ul, ol, table, blockquote';

function getArticleAdParagraphs(container) {
  if (!container || typeof container.querySelectorAll !== 'function') return [];
  return Array.from(container.querySelectorAll('p')).filter((paragraph) => {
    const excluded = paragraph.closest(ARTICLE_NON_EDITORIAL_SELECTOR);
    return (!excluded || !container.contains(excluded))
      && paragraph.closest('.body-description') === container
      && getParagraphWordCount(paragraph) > 0;
  });
}

function getArticleAdBoundary(paragraph, container) {
  // Keep complete CMS list items and semantic lists/tables/quotes together.
  // Ordinary editorial wrappers can still receive ads between paragraphs.
  let anchor = paragraph;
  for (let parent = paragraph.parentElement; parent && parent !== container; parent = parent.parentElement) {
    if (parent.matches(ARTICLE_ATOMIC_SECTION_SELECTOR)) anchor = parent;
  }
  return anchor;
}

function resolveWordBasedArticleAdPlacements(container, options = {}) {
  const wordInterval = Math.max(1, Number(options.wordInterval) || 250);
  const maxCount = Math.max(1, Number(options.maxCount) || 6);
  const boundaries = new Map();
  getArticleAdParagraphs(container).forEach((paragraph) => {
    const anchor = getArticleAdBoundary(paragraph, container);
    boundaries.set(anchor, (boundaries.get(anchor) || 0) + getParagraphWordCount(paragraph));
  });

  const placements = [];
  let cumulativeWords = 0;
  let nextThreshold = wordInterval;
  boundaries.forEach((wordCount, anchor) => {
    cumulativeWords += wordCount;
    if (cumulativeWords < nextThreshold || nextThreshold > wordInterval * maxCount) return;
    placements.push({ anchor, cumulativeWords });
    // A long paragraph or section can cross several thresholds. It gets one
    // break, and all consumed thresholds advance together to avoid stacked ads.
    nextThreshold = (Math.floor(cumulativeWords / wordInterval) + 1) * wordInterval;
  });
  return placements;
}

function getDirectListicleItems(container, selector = DEFAULT_LISTICLE_SELECTOR) {
  if (!container || !container.children) return [];
  return Array.from(container.children).filter((child) => (
    child && typeof child.matches === 'function' && child.matches(selector)
  ));
}

function resolveListicleAdPlacements(container, options = {}) {
  const items = getDirectListicleItems(container, options.selector);
  const minItems = Number.isFinite(Number(options.minItems))
    ? Math.max(1, Number(options.minItems))
    : 8;
  if (items.length < minItems) return [];

  const firstAfterItem = Number.isFinite(Number(options.firstAfterItem))
    ? Math.max(1, Number(options.firstAfterItem))
    : 2;
  const itemInterval = Number.isFinite(Number(options.itemInterval))
    ? Math.max(1, Number(options.itemInterval))
    : 3;
  const maxCount = Number.isFinite(Number(options.maxCount))
    ? Math.max(1, Number(options.maxCount))
    : 6;
  const placements = [];

  for (
    let itemNumber = firstAfterItem;
    itemNumber <= items.length && placements.length < maxCount;
    itemNumber += itemInterval
  ) {
    placements.push({
      anchor: items[itemNumber - 1],
      itemNumber,
      slotIndex: placements.length + 1
    });
  }

  return placements;
}

function getParagraphWordCount(paragraph) {
  if (!paragraph) return 0;
  const text = String(paragraph.innerText || paragraph.textContent || '').trim();
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

function resolveUpperArticleAdPlacement(paragraphs, options = {}) {
  const paragraphList = Array.from(paragraphs || []).filter(Boolean);
  const upperWordThreshold = Number.isFinite(Number(options.upperWordThreshold))
    ? Math.max(1, Number(options.upperWordThreshold))
    : 100;
  const existingFirstAdWordThreshold = Number.isFinite(
    Number(options.existingFirstAdWordThreshold)
  )
    ? Math.max(upperWordThreshold + 1, Number(options.existingFirstAdWordThreshold))
    : 250;
  const minParagraphGap = Number.isFinite(Number(options.minParagraphGap))
    ? Math.max(1, Number(options.minParagraphGap))
    : 2;
  let cumulativeWords = 0;
  let upperBoundary = null;
  let existingBoundary = null;

  paragraphList.forEach((paragraph, index) => {
    cumulativeWords += getParagraphWordCount(paragraph);
    if (!upperBoundary && cumulativeWords >= upperWordThreshold) {
      upperBoundary = { anchor: paragraph, index, cumulativeWords };
    }
    if (!existingBoundary && cumulativeWords >= existingFirstAdWordThreshold) {
      existingBoundary = { anchor: paragraph, index, cumulativeWords };
    }
  });

  if (
    !upperBoundary
    || !existingBoundary
    || existingBoundary.index - upperBoundary.index < minParagraphGap
  ) {
    return null;
  }

  return {
    anchor: upperBoundary.anchor,
    paragraphNumber: upperBoundary.index + 1,
    cumulativeWords: upperBoundary.cumulativeWords,
    nextAdParagraphNumber: existingBoundary.index + 1,
    totalWords: cumulativeWords
  };
}

module.exports = {
  getArticleAdParagraphs,
  getArticleAdBoundary,
  getParagraphWordCount,
  getDirectListicleItems,
  resolveWordBasedArticleAdPlacements,
  resolveUpperArticleAdPlacement,
  resolveListicleAdPlacements
};
